import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { RotateCcw, Calendar, User } from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'

import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from '@/components/application/side-panel'
import { Button } from '@/components/base/buttons'
import { AlertDialog } from '@/components/ui/AlertDialog'
import type { Revision } from '@/types/revision'
import { FieldDiffGroup } from './FieldDiff'
import {
  computeRevisionDiff,
  groupDiffsByCategory,
} from './diffUtils'

interface RevisionCompareDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback when the drawer should close */
  onClose: () => void
  /** The revision to compare (the state at that point in time) */
  revision: Revision | null
  /** The current assistant data (to compare against) */
  currentData: Record<string, unknown>
  /** Author display name */
  authorName?: string
}

/**
 * Side drawer that shows the comparison between a revision and current state
 * with option to restore the revision data into the form
 */
export function RevisionCompareDrawer({
  isOpen,
  onClose,
  revision,
  currentData,
  authorName,
}: RevisionCompareDrawerProps) {
  const { t } = useTranslation()
  const { reset } = useFormContext()
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)

  // Compute diffs between revision and current data
  const diffs = useMemo(() => {
    if (!revision) return []
    return computeRevisionDiff(
      revision.data as Record<string, unknown>,
      currentData
    )
  }, [revision, currentData])

  // Group diffs by category
  const groupedDiffs = useMemo(() => {
    return groupDiffsByCategory(diffs)
  }, [diffs])

  // Get categories with changes
  const categoriesWithChanges = useMemo(() => {
    return (Object.keys(groupedDiffs) as Array<keyof typeof groupedDiffs>).filter(
      (cat) => groupedDiffs[cat].length > 0
    )
  }, [groupedDiffs])

  // Handle restore action
  const handleRestore = () => {
    if (!revision) return

    // Reset form with revision data
    // We need to carefully merge revision data since it may not have all fields
    const revisionData = revision.data as Record<string, unknown>

    // Reset the form with the revision data
    // keepDefaultValues: true ensures the original server data remains as the default values,
    // so the form will be marked as "dirty" and the Save buttons will be enabled
    reset(revisionData, { keepDefaultValues: true })

    // Close dialog and drawer
    setShowRestoreConfirm(false)
    onClose()

    // Notify user
    toast.info(t('resources.assistants.revisions.comparison.restored'), {
      duration: 5000,
    })
  }

  if (!revision) return null

  const formattedDate = format(new Date(revision.date), 'PPpp')

  // Get translated category labels
  const getCategoryLabel = (category: string) => {
    return t(`resources.assistants.revisions.categories.${category}`)
  }

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onOpenChange={(open) => !open && onClose()}
        onClose={onClose}
        side="right"
        size="2xl"
        variant="details"
      >
        <SidePanelContent>
          <SidePanelHeader
            title={t('resources.assistants.revisions.comparison.title')}
            onClose={onClose}
          />

          <SidePanelBody>
            {/* Revision metadata */}
            <div className="mb-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formattedDate}</span>
                </div>
                {authorName && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{authorName}</span>
                  </div>
                )}
              </div>
              {revision.message && (
                <p className="mt-2 font-medium text-gray-900 dark:text-gray-100">
                  {revision.message}
                </p>
              )}
              {revision.description && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {revision.description}
                </p>
              )}
            </div>

            {/* Diff summary */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {diffs.length === 1
                  ? t('resources.assistants.revisions.comparison.fields_changed', { count: diffs.length })
                  : t('resources.assistants.revisions.comparison.fields_changed_plural', { count: diffs.length })}
              </h3>
            </div>

            {/* No changes state */}
            {diffs.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-green-100 p-4 dark:bg-green-900/30">
                  <RotateCcw className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">
                  {t('resources.assistants.revisions.comparison.no_differences')}
                </p>
              </div>
            )}

            {/* Grouped diffs */}
            {categoriesWithChanges.map((category) => (
              <FieldDiffGroup
                key={category}
                label={getCategoryLabel(category)}
                diffs={groupedDiffs[category]}
                defaultExpanded={true}
              />
            ))}
          </SidePanelBody>

          <SidePanelFooter>
            <Button color="secondary" onPress={onClose}>
              {t('resources.assistants.revisions.comparison.close')}
            </Button>
            <Button
              color="primary"
              onPress={() => setShowRestoreConfirm(true)}
              isDisabled={diffs.length === 0}
              iconLeading={RotateCcw}
            >
              {t('resources.assistants.revisions.comparison.restore_button')}
            </Button>
          </SidePanelFooter>
        </SidePanelContent>
      </SidePanel>

      {/* Restore confirmation dialog */}
      <AlertDialog
        isOpen={showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(false)}
        onConfirm={handleRestore}
        title={t('resources.assistants.revisions.comparison.restore_title')}
        description={t('resources.assistants.revisions.comparison.restore_message')}
        confirmLabel={t('resources.assistants.revisions.comparison.restore_confirm')}
      />
    </>
  )
}

export default RevisionCompareDrawer
